import type { VoicedChord } from '@tunasong/music-lib'
import { classifyVoiceMotion, semitoneDistance } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import type { FC } from 'react'
import styles from './voicing-piano-roll.module.css'

export interface VoicingPianoRollProps {
  /** Exactly 2 voiced chords (low → high pitches each). */
  voicing: readonly VoicedChord[]
  /** Optional chord labels rendered above each column (e.g. ['I', 'V']). */
  columnLabels?: readonly [string, string]
  className?: string
}

// y-axis covers the Bowie-locked range [C2, B4] across all 12 keys × 4 pairs.
// Low bound is C2 (not G2): the IV→V awkward bass reaches F2 in C major and
// C2 transposed to G major — both below G2 (scope memo §224). A G2 floor would
// clip those rects below the plot, breaking the parallel-fifths trap pair.
const Y_AXIS_LOW: NoteName = 'C2' as NoteName
const Y_AXIS_HIGH: NoteName = 'B4' as NoteName
const AXIS_SPAN = semitoneDistance(Y_AXIS_LOW, Y_AXIS_HIGH) // 35 semitones

const VOICE_LANE_CLASS = ['voiceBass', 'voiceMid', 'voiceTop'] as const

/** 0 % = top of the plot (B4); 100 % = bottom (G2). */
const yPercent = (pitch: NoteName): number =>
  ((AXIS_SPAN - semitoneDistance(Y_AXIS_LOW, pitch)) / AXIS_SPAN) * 100

const motionClass = (from: NoteName, to: NoteName): string => {
  const motion = classifyVoiceMotion(from, to)
  if (motion === 'common-tone') {
    return styles.commonToneHeld
  }
  if (motion === 'leap') {
    return styles.leap
  }
  return styles.step
}

const VOICE_NAME = ['Bass', 'Middle', 'Top'] as const

/** Screen-reader phrase naming the concept (not pixels) for one voice's motion. */
const motionPhrase = (voiceIndex: number, from: NoteName, to: NoteName): string => {
  const name = VOICE_NAME[voiceIndex] ?? `Voice ${voiceIndex + 1}`
  const motion = classifyVoiceMotion(from, to)
  if (motion === 'common-tone') {
    return `${name}: ${from} held (common tone).`
  }
  const direction = semitoneDistance(from, to) > 0 ? 'up' : 'down'
  return `${name}: ${motion} ${direction}, ${from} to ${to}.`
}

/**
 * The first Tunasong pitch-visualization component. Renders a 2-column piano
 * roll for a voice-leading pair: each voice gets a note rect per chord plus a
 * connecting motion line coloured by motion type (common-tone / step / leap).
 *
 * v1 ships a functional default using design tokens; Joni iterates on the
 * Storybook entries post-v1 (M3.2).
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §8.5
 */
export const VoicingPianoRoll: FC<VoicingPianoRollProps> = props => {
  const { voicing, columnLabels, className } = props
  if (voicing.length !== 2) {
    return <div className={styles.empty}>Voicing must have exactly 2 chords.</div>
  }
  const [a, b] = voicing
  const voiceCount = Math.min(a.pitches.length, b.pitches.length)

  const transition = columnLabels ? `${columnLabels[0]} to ${columnLabels[1]}` : 'chord pair'
  const motionSummary = Array.from({ length: voiceCount }, (_, v) =>
    motionPhrase(v, a.pitches[v], b.pitches[v])
  ).join(' ')
  const ariaLabel = `Voice motion, ${transition}. ${motionSummary}`

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {columnLabels ? (
        <div className={styles.columnLabels} aria-hidden="true">
          <span>{columnLabels[0]}</span>
          <span>{columnLabels[1]}</span>
        </div>
      ) : null}
      <svg
        className={styles.svg}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
      >
        {Array.from({ length: voiceCount }, (_, v) => (
          <g key={v} data-testid="vl-voice-lane" className={styles[VOICE_LANE_CLASS[v] ?? 'voiceMid']}>
            <rect data-testid="vl-note-rect" x={8} y={yPercent(a.pitches[v]) - 2} width={28} height={4} />
            <rect data-testid="vl-note-rect" x={64} y={yPercent(b.pitches[v]) - 2} width={28} height={4} />
            <line
              data-testid="vl-voice-motion-line"
              x1={36}
              y1={yPercent(a.pitches[v])}
              x2={64}
              y2={yPercent(b.pitches[v])}
              className={motionClass(a.pitches[v], b.pitches[v])}
            />
          </g>
        ))}
      </svg>
      <div className={styles.columns}>
        {[a, b].map((chord, c) => (
          <div key={c} data-testid="vl-chord-column" className={styles.column}>
            {chord.pitches.map((p, v) => (
              <span key={v} className={styles[VOICE_LANE_CLASS[v] ?? 'voiceMid']}>
                {p}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
